import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Collapse, Switch, Tooltip } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { clinicalAnalyticsActions } from 'store/clinical';
import { DateTypeSelector, EditWithIcon } from 'components';
import { canEditTargetStatus } from '../constants';
import { GoalTargetStatus, isTargetStatusLocked, MasteredTargetTooltip } from '../targets/goalTargetStatus';
import { graphValueModeEnums, Images, makeCapitalize } from 'utils';
import { GraphValueModeToggle, toValueModeParam } from './components/graphValueModeToggle';
import { InsightsDonutChart } from './components/apexDonutChart';
import { ChartInfoPopover } from './components/chartInfoPopover';
import { passesMasteryThreshold, TargetProgressChart } from './components/targetProgressChart';
import { buildBucketTooltipHtml, buildEventBuckets, EVENT_TOOLTIP_FONT_FAMILY, formatInsightDate } from './components/chartEventHelpers';
import { buildAverageLine, buildLinearTrendLine } from './components/trendLine.helpers';
import {
    buildTargetLegendOption,
    formatChartValueByDataType,
    getChartValueLabelByDataType,
    hasChartValue,
    isPercentageDataType,
    TARGET_LEGEND_GRID_BOTTOM,
} from './components/dataTypeChart.helpers';
import { displayModeEnums, timeRangePresets, targetStatusColors, getGoalMasteryLine } from './constants';
import { GET_GOAL_DRILLDOWN_SUMMARY, GET_GOAL_DRILLDOWN_TARGETS } from 'store/clinical/clinicalAnalytics/clinicalAnalytics.types';
import moment from 'moment';

const FONT_FAMILY = '"GT Walsheim Pro", "Open Sans", sans-serif';
const DEFAULT_TIME_RANGE = 1;

const buildPresetDateRange = (months, baseDate = moment()) => {
    const date = baseDate.clone();

    return {
        start: date.clone().subtract(months, 'months').format('YYYY-MM-DD'),
        end: date.format('YYYY-MM-DD'),
    };
};

const clampRangeToToday = (startDate, endDate) => {
    const today = moment().endOf('day');
    const safeStart = startDate ? moment(startDate) : null;
    const safeEnd = endDate ? moment(endDate) : null;

    if (!safeStart?.isValid() || !safeEnd?.isValid()) {
        return null;
    }

    const clampedStart = moment.min(safeStart, today);
    const clampedEnd = moment.min(safeEnd, today);

    return {
        start: clampedStart.format('YYYY-MM-DD'),
        end: clampedEnd.format('YYYY-MM-DD'),
    };
};

const triggerDownload = (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const roundRect = (context, x, y, width, height, radius) => {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
};

const getTimeAxisLabel = (categories = []) => {
    const sample = categories.find(Boolean);
    if (!sample) return 'Time';
    if (sample.includes('-W')) return 'Time';
    if (sample.length > 7) return 'Time';
    return 'Time';
};

const createExportCanvas = (width, height) => {
    const scale = window.devicePixelRatio > 1 ? 2 : 1;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('Canvas context not available');
    }

    context.scale(scale, scale);
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, width, height);

    return { canvas, context };
};

const exportStatusDistributionCard = async ({ chartRef, labels, values, colors, fileName }) => {
    const chartInstance = chartRef?.current?.getEchartsInstance?.();
    if (!chartInstance) return;

    const chartImage = await loadImage(
        chartInstance.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#FFFFFF',
            excludeComponents: ['toolbox'],
        })
    );

    const width = 720;
    const height = 340;
    const { canvas, context } = createExportCanvas(width, height);

    roundRect(context, 0.5, 0.5, width - 1, height - 1, 12);
    context.fillStyle = '#FFFFFF';
    context.fill();
    context.strokeStyle = '#EAECF0';
    context.stroke();

    context.fillStyle = '#1D2939';
    context.font = `600 15px ${FONT_FAMILY}`;
    context.fillText('Target Status Distribution', 20, 30);

    context.drawImage(chartImage, 24, 56, 220, 220);

    const legendX = 276;
    let legendY = 58;

    labels.forEach((label, index) => {
        roundRect(context, legendX, legendY, 420, 36, 10);
        context.fillStyle = '#FCFCFD';
        context.fill();
        context.strokeStyle = '#EAECF0';
        context.stroke();

        context.beginPath();
        context.arc(292, legendY + 18, 5, 0, Math.PI * 2);
        context.fillStyle = colors[index];
        context.fill();

        context.fillStyle = '#344054';
        context.font = `500 13px ${FONT_FAMILY}`;
        context.fillText(label, 306, legendY + 22);

        context.fillStyle = '#1D2939';
        context.font = `700 15px ${FONT_FAMILY}`;
        const valueText = `${values[index] || 0}`;
        const valueWidth = context.measureText(valueText).width;
        context.fillText(valueText, 682 - valueWidth, legendY + 22);

        legendY += 46;
    });

    triggerDownload(canvas.toDataURL('image/png'), fileName);
};

const exportStaffFocusCard = async ({ chartRef, fileName }) => {
    const chartInstance = chartRef?.current?.getEchartsInstance?.();
    if (!chartInstance) return;

    const chartImage = await loadImage(
        chartInstance.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#FFFFFF',
            excludeComponents: ['toolbox'],
        })
    );

    const width = 720;
    const height = 320;
    const { canvas, context } = createExportCanvas(width, height);

    roundRect(context, 0.5, 0.5, width - 1, height - 1, 12);
    context.fillStyle = '#FFFFFF';
    context.fill();
    context.strokeStyle = '#EAECF0';
    context.stroke();

    context.fillStyle = '#1D2939';
    context.font = `600 15px ${FONT_FAMILY}`;
    context.fillText('Staff Focus', 20, 30);

    context.fillStyle = '#667085';
    context.font = `400 12px ${FONT_FAMILY}`;
    context.fillText('Where staff time is needed right now', 20, 50);

    context.drawImage(chartImage, 18, 72, 684, 220);

    triggerDownload(canvas.toDataURL('image/png'), fileName);
};

const getChangePointIndexes = (data = [], displayMode) => {
    if (!data.length) return new Set();

    const indexes = new Set([data.length - 1]);
    const valueKey = displayMode === displayModeEnums.PERCENT ? 'value' : 'valueCount';

    data.forEach((point, index) => {
        if (point?.[valueKey] !== null && point?.[valueKey] !== undefined) {
            indexes.add(index);
        }
    });

    return indexes;
};

const hasTargetSessionData = (target) =>
    Array.isArray(target?.progressData) && target.progressData.some((point) => point?.value !== null && point?.value !== undefined);

const getStatusClassName = (status) => (status ? `status-${String(status).toLowerCase()}` : 'status-default');

/** Keys of the collapsible chart cards; per-target cards use `target:<targetId>`. */
const chartCardKeys = {
    CUMULATIVE: 'cumulative',
    TARGETS_PASSING_MASTERY: 'targetsPassingMastery',
    ALL_TARGETS: 'allTargets',
    STATUS_DISTRIBUTION: 'statusDistribution',
    STAFF_FOCUS: 'staffFocus',
};
const targetCardKey = (targetId) => `target:${targetId}`;

/** Chevron that collapses / expands a chart card; same icons as the goals table's expand toggle. */
const ChartCollapseToggle = ({ collapsed, onToggle }) => (
    <EditWithIcon icon={collapsed ? Images.dropDown : Images.dropUp} handleClick={onToggle} type="button" styles={{ flexShrink: 0 }} />
);

const buildAggregateTargetProgressOption = ({ targets, events = [] }) => {
    if (!targets?.length) return null;

    const categories = Array.from(new Set(targets.flatMap((target) => (target?.progressData || []).map((point) => point.date))));

    if (!categories.length) return null;

    const palette = ['#0C66E4', '#12B76A', '#F79009', '#7A5AF8', '#EE46BC', '#17B26A', '#F04438', '#2E90FA'];
    const dataType = targets.find((target) => target?.dataType)?.dataType;
    const valueLabel = getChartValueLabelByDataType(dataType);
    const isPercentType = isPercentageDataType(dataType);
    const series = targets.map((target, index) => {
        const pointMap = new Map((target?.progressData || []).map((point) => [point.date, point.value]));

        return {
            name: target.targetName,
            type: 'line',
            data: categories.map((category) => pointMap.get(category) ?? null),
            connectNulls: true,
            smooth: false,
            symbol: 'circle',
            showSymbol: true,
            symbolSize: (value) => (value === null || value === undefined ? 0 : 5),
            lineStyle: { color: palette[index % palette.length], width: 2 },
            itemStyle: { color: palette[index % palette.length], borderColor: '#fff', borderWidth: 2 },
        };
    });
    const numericValues = series.flatMap((item) => item.data).filter((value) => value !== null && value !== undefined);
    const yAxisMax = Math.max(...numericValues, 0);
    const hasFractionalValues = numericValues.some((value) => !Number.isInteger(Number(value)));
    const yAxisDisplayMax = isPercentType
        ? 100
        : Math.max(Math.ceil(yAxisMax * 1.12), Math.ceil(yAxisMax) + 1, 1);
    const filteredEvents = events.filter((event) => event.type === 'Client' || event.type === 'Goal');
    const eventBuckets = buildEventBuckets(filteredEvents, categories);
    const eventBucketMap = new Map(eventBuckets.map((bucket) => [bucket.key, bucket]));
    const eventMarkerY = Math.max(yAxisMax, yAxisDisplayMax * 0.9);
    const markLineData = eventBuckets.map((bucket) => ({
        xAxis: bucket.key,
        lineStyle: {
            color: bucket.color,
            type: 'dashed',
            width: 1.5,
        },
        label: { show: false },
    }));
    const eventMarkerSeries = eventBuckets.some((bucket) => bucket.count > 1)
        ? [
              {
                  name: 'Event Counts',
                  type: 'scatter',
                  silent: true,
                  data: eventBuckets
                      .filter((bucket) => bucket.count > 1)
                      .map((bucket) => ({
                          value: [bucket.key, eventMarkerY],
                          count: bucket.count,
                          itemStyle: { color: bucket.color },
                          label: {
                              show: true,
                              formatter: `${bucket.count}`,
                              color: '#FFFFFF',
                              fontSize: 10,
                              fontWeight: 700,
                              fontFamily: FONT_FAMILY,
                          },
                      })),
                  symbol: 'circle',
                  symbolSize: 18,
                  z: 6,
              },
          ]
        : [];
    const eventHoverSeries =
        eventBuckets.length > 0
            ? [
                  {
                      name: 'Event Hover',
                      type: 'bar',
                      data: categories.map((category) => (eventBucketMap.has(category) ? yAxisDisplayMax : 0)),
                      barWidth: 12,
                      barGap: '-100%',
                      itemStyle: { color: 'rgba(0, 0, 0, 0)' },
                      emphasis: { itemStyle: { color: 'rgba(0, 0, 0, 0)' } },
                      tooltip: { show: false },
                      z: 1,
                  },
              ]
            : [];

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line' },
            extraCssText: `box-shadow:none;background:transparent;border:none;padding:0;font-family:${EVENT_TOOLTIP_FONT_FAMILY};`,
            textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
            formatter: (params) => {
                if (!params?.length) return '';
                const axisValue = params[0].axisValue;
                const label = formatInsightDate(axisValue, 'MMM YYYY', 'DD MMM YYYY');
                const lineRows = params
                    .filter((param) => param.seriesType === 'line' && hasChartValue(param.value))
                    .map(
                        (param) => `
                        <div style="display:flex; align-items:center; gap:6px;">
                            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${param.color};"></span>
                            <p style="color:#344054; font-size:12px; font-weight:400; line-height:18px; margin:0;">
                                ${param.seriesName}:
                                <span style="color:#344054; font-size:12px; font-weight:600; line-height:18px;"> ${
                                    hasFractionalValues && !isPercentType && !Number.isInteger(Number(param.value))
                                        ? Number(param.value).toFixed(2)
                                        : formatChartValueByDataType(param.value, dataType)
                                }</span>
                            </p>
                        </div>
                    `
                    )
                    .join('') ||
                    // Only targets with data that day are listed; on a day none has any, one line says so.
                    '<p style="color:#667085; font-size:12px; line-height:18px; margin:0;">No session data</p>';
                const eventTooltip = buildBucketTooltipHtml(eventBucketMap.get(axisValue));

                return `
                    <div style="padding:12px 8px; background:white; border-radius:8px; box-shadow:0 0 6px 0 rgba(138, 138, 138, 0.24); margin-bottom:${eventTooltip ? '8px' : '0'}; font-family:${EVENT_TOOLTIP_FONT_FAMILY};">
                        <p style="color:#0C66E4; font-size:12px; font-weight:600; line-height:18px; margin:0 0 6px;">${label}</p>
                        <div style="display:flex; flex-direction:column; gap:4px;">${lineRows}</div>
                    </div>
                    ${eventTooltip}
                `;
            },
        },
        legend: buildTargetLegendOption({ names: series.map((item) => item.name), fontFamily: FONT_FAMILY }),
        grid: { left: '7%', right: '4%', top: '8%', bottom: TARGET_LEGEND_GRID_BOTTOM, containLabel: true },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: categories,
            name: 'Time',
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: {
                color: '#667085',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
            },
            axisLine: { lineStyle: { color: '#E5E7EB' } },
            axisTick: { show: false },
            axisLabel: {
                color: '#98A2B3',
                fontSize: 11,
                fontFamily: FONT_FAMILY,
                formatter: (value) => formatInsightDate(value),
            },
        },
        yAxis: {
            type: 'value',
            min: 0,
            max: yAxisDisplayMax,
            minInterval: isPercentType || hasFractionalValues ? undefined : 1,
            name: valueLabel,
            nameLocation: 'middle',
            nameGap: 52,
            nameRotate: 90,
            nameTextStyle: {
                color: '#667085',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
            },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: '#98A2B3',
                fontSize: 11,
                fontFamily: FONT_FAMILY,
                formatter: (value) =>
                    hasFractionalValues && !isPercentType ? Number(value).toFixed(2) : formatChartValueByDataType(value, dataType),
            },
            splitLine: { lineStyle: { color: '#F2F4F7' } },
        },
        series: [
            ...series.map((item) => ({
                ...item,
                markLine: markLineData.length > 0 ? { symbol: 'none', data: markLineData } : undefined,
            })),
            ...eventHoverSeries,
            ...eventMarkerSeries,
        ],
    };
};

/**
 * Per-bucket count of targets whose SESSION value crossed THEIR OWN mastery
 * threshold in that bucket — distinct from "Cumulative Goal Progress" above,
 * which tracks each target's STATUS (a one-time flip to MASTERED). This
 * answers "how many targets passed mastery in this session", not
 * cumulative — a target can appear here in one bucket and not the next.
 *
 * Reuses the same bucket dates as the cumulative chart (both come from the
 * same date range/groupBy) so the two charts' x-axes line up, and reuses
 * each target's already-fetched `progressData`/`mastery` — no extra
 * request needed.
 */
const buildTargetsPassingMasteryOption = ({ dates, targets }) => {
    const targetsWithMastery = (targets || []).filter(
        (target) => target?.mastery?.count !== null && target?.mastery?.count !== undefined
    );
    const totalTargets = targetsWithMastery.length;

    if (!dates?.length || !totalTargets) return { option: null, totalTargets: 0 };

    const MAX_NAMES_SHOWN = 4;
    const MAX_NAME_CHARS = 22;
    const truncateName = (name) => (name?.length > MAX_NAME_CHARS ? `${name.slice(0, MAX_NAME_CHARS - 1)}…` : name);
    const passedNamesByBucket = dates.map((date) => {
        const names = [];
        for (const target of targetsWithMastery) {
            const point = target.progressData?.find((p) => p.date === date);
            if (point?.value === null || point?.value === undefined) continue;
            if (passesMasteryThreshold(point.value, target.mastery.count, target.mastery.direction)) {
                names.push(makeCapitalize(target.targetName));
            }
        }
        return names;
    });
    const seriesData = passedNamesByBucket.map((names) => names.length);

    const option = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
            // Long/many target names must never blow up the tooltip — wrap
            // within a fixed max-width instead of stretching or overflowing.
            extraCssText: 'max-width: 240px; white-space: normal;',
            formatter: (params) => {
                if (!params?.length) return '';
                const label = formatInsightDate(params[0].axisValue, 'MMM YYYY', 'DD MMM YYYY');
                const names = passedNamesByBucket[params[0].dataIndex] || [];
                const shown = names.slice(0, MAX_NAMES_SHOWN).map(truncateName);
                const extra = names.length - shown.length;
                const namesHtml = shown.length
                    ? `<div style="margin-top:2px; color:#475467; font-size:11px; line-height:16px; word-break:break-word;">${shown.join(', ')}${
                          extra > 0 ? ` <span style="color:#98A2B3;">+${extra} more</span>` : ''
                      }</div>`
                    : '';
                return `<b style="color:#0C66E4">${label}</b><br/>Targets achieving mastery: <b>${params[0].value}/${totalTargets}</b>${namesHtml}`;
            },
        },
        grid: { left: '3%', right: '4%', top: '8%', bottom: '18%', containLabel: true },
        xAxis: {
            type: 'category',
            data: dates,
            axisLine: { lineStyle: { color: '#E5E7EB' } },
            axisTick: { show: false },
            axisLabel: {
                color: '#98A2B3',
                fontSize: 11,
                fontFamily: FONT_FAMILY,
                formatter: (v) => formatInsightDate(v),
            },
        },
        yAxis: {
            type: 'value',
            min: 0,
            max: totalTargets,
            minInterval: 1,
            name: 'Targets',
            nameLocation: 'middle',
            nameGap: 28,
            nameRotate: 90,
            nameTextStyle: { color: '#667085', fontSize: 12, fontWeight: 600, fontFamily: FONT_FAMILY },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#98A2B3', fontSize: 11, fontFamily: FONT_FAMILY },
            splitLine: { lineStyle: { color: '#F2F4F7' } },
        },
        series: [
            {
                name: 'Targets Passing Mastery',
                type: 'bar',
                data: seriesData,
                barMaxWidth: 24,
                itemStyle: { color: '#12B76A', borderRadius: [4, 4, 0, 0] },
            },
        ],
    };

    return { option, totalTargets };
};

export const GoalProgressTab = ({ goalId, clientId, requestParams }) => {
    const dispatch = useDispatch();
    const drilldown = useSelector((state) => state.clinicalAnalytics.goalDrilldown);
    const client = useSelector((state) => state.client.clientItemInfo);
    const goalById = useSelector((state) => state.goals.goalById);
    const isSummaryLoading = useSelector((state) => state.httpOnLoad?.some((item) => item?.loadingType === GET_GOAL_DRILLDOWN_SUMMARY));
    const isTargetsLoading = useSelector((state) => state.httpOnLoad?.some((item) => item?.loadingType === GET_GOAL_DRILLDOWN_TARGETS));
    const cumulativeChartRef = useRef(null);
    const targetsPassingMasteryChartRef = useRef(null);
    const statusDistributionChartRef = useRef(null);
    const staffFocusChartRef = useRef(null);
    const aggregateTargetChartRef = useRef(null);
    const targetChartRefs = useRef({});

    const resolvedClientId = clientId || client?.id;

    const [activeRangePreset, setActiveRangePreset] = useState(DEFAULT_TIME_RANGE);
    const [dateRange, setDateRange] = useState(() => buildPresetDateRange(DEFAULT_TIME_RANGE));
    const [showMastery, setShowMastery] = useState(true);
    const [showTrendLine, setShowTrendLine] = useState(true);
    const [showClientEvents, setShowClientEvents] = useState(true);
    const [showGoalEvents, setShowGoalEvents] = useState(true);
    const [showAggregateClientEvents, setShowAggregateClientEvents] = useState(true);
    const [showAggregateGoalEvents, setShowAggregateGoalEvents] = useState(true);
    const [targetFilter, setTargetFilter] = useState('');
    const [displayMode, setDisplayMode] = useState(displayModeEnums.PERCENT);
    const [enableGrouping, setEnableGrouping] = useState(false);
    const [valueMode, setValueMode] = useState(graphValueModeEnums.DEFAULT);
    // The goal's data type decides which "Show" options exist (Total/Average, All/First trial).
    const goalDataType =
        (goalById?.id === goalId ? goalById?.dataType : undefined) ||
        drilldown?.targetProgress?.find((target) => target?.dataType)?.dataType;
    const valueModeParam = toValueModeParam(goalDataType, valueMode);
    // Cards are expanded by default; a key here means that card is collapsed.
    const [collapsedCards, setCollapsedCards] = useState({});
    const isCollapsed = (key) => Boolean(collapsedCards[key]);
    const toggleCard = (key) => setCollapsedCards((prev) => ({ ...prev, [key]: !prev[key] }));
    // Status changes on a target card need the full target (name + description are saved with it) from the loaded goal.
    const loadedGoal = goalById?.id === goalId ? goalById : null;
    const canChangeStatus = Boolean(loadedGoal) && canEditTargetStatus(loadedGoal);

    const fetchDrilldown = () => {
        if (resolvedClientId && goalId) {
            dispatch(
                clinicalAnalyticsActions.getGoalDrilldown(resolvedClientId, goalId, {
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                    targetStatus: targetFilter || undefined,
                    groupBy: enableGrouping ? undefined : 'day',
                    valueMode: valueModeParam,
                })
            );
        }
    };

    useEffect(() => {
        fetchDrilldown();
    }, [dateRange.end, dateRange.start, dispatch, enableGrouping, goalId, resolvedClientId, targetFilter, valueModeParam]);

    useEffect(() => {
        return () => {
            dispatch(clinicalAnalyticsActions.resetClinicalAnalytics());
        };
    }, []);

    const handleTimeRangeChange = (months) => {
        setActiveRangePreset(months);
        setDateRange(buildPresetDateRange(months));
    };

    const handleDateRangeChange = (params) => {
        if (params?.startDate && params?.endDate) {
            const nextRange = clampRangeToToday(params.startDate, params.endDate);
            if (!nextRange) return;
            setDateRange(nextRange);
            setActiveRangePreset(null);
        }
    };

    const handleGraphRangeSelect = (nextRange) => {
        if (!nextRange?.startDate || !nextRange?.endDate) return;

        setDateRange({
            start: nextRange.startDate,
            end: nextRange.endDate,
        });
        setActiveRangePreset(null);
    };

    const dateSelectorFilters = useMemo(() => ({ startDate: dateRange.start, endDate: dateRange.end }), [dateRange.end, dateRange.start]);

    const getChartExportMeta = (chartRef, prefix) => {
        const chartInstance = chartRef?.current?.getEchartsInstance?.();
        if (!chartInstance) return null;

        return {
            chartInstance,
            fileBaseName: `${prefix}-${goalId || resolvedClientId || 'chart'}-${moment().format('YYYYMMDD-HHmmss')}`,
        };
    };

    const handleDownloadImage = (chartRef = cumulativeChartRef, prefix = 'goal-progress') => {
        const exportMeta = getChartExportMeta(chartRef, prefix);
        if (!exportMeta) return;

        try {
            const imageUrl = exportMeta.chartInstance.getDataURL({
                type: 'png',
                pixelRatio: 2,
                backgroundColor: '#FFFFFF',
                excludeComponents: ['toolbox'],
            });

            triggerDownload(imageUrl, `${exportMeta.fileBaseName}.png`);
        } catch (error) {
            console.error('Failed to export chart as image', error);
        }
    };

    const handleDownloadTargetImage = (targetId, targetName) => {
        handleDownloadImage({ current: targetChartRefs.current[targetId] }, `${targetName || 'target-progress'}-${targetId || 'chart'}`);
    };

    const handleDownloadCardImage = async (type) => {
        try {
            const fileName = `${type}-${goalId || resolvedClientId || 'chart'}-${moment().format('YYYYMMDD-HHmmss')}.png`;

            if (type === 'target-status-distribution') {
                await exportStatusDistributionCard({
                    chartRef: statusDistributionChartRef,
                    labels: statusLabels,
                    values: statusSeries,
                    colors: statusDonutColors,
                    fileName,
                });
                return;
            }

            if (type === 'staff-focus') {
                await exportStaffFocusCard({
                    chartRef: staffFocusChartRef,
                    fileName,
                });
            }
        } catch (error) {
            console.error('Failed to export card as image', error);
        }
    };

    const hasSummaryData = Boolean(drilldown?.cumulativeProgress || drilldown?.targetStatusDistribution);
    const hasTargetData = Array.isArray(drilldown?.targetProgress);

    if (!hasSummaryData && !hasTargetData && (isSummaryLoading || isTargetsLoading)) {
        return (
            <div className="insights-loading">
                <CircularProgress size={32} />
            </div>
        );
    }

    const { cumulativeProgress, goalMastery, targetStatusDistribution = [], targetProgress = [] } = drilldown || {};
    const sortedTargetProgress = [...targetProgress].sort((left, right) => {
        const leftHasData = hasTargetSessionData(left);
        const rightHasData = hasTargetSessionData(right);

        if (leftHasData === rightHasData) {
            return (left?.targetName || '').localeCompare(right?.targetName || '');
        }
        return leftHasData ? -1 : 1;
    });

    const statusSeries = targetStatusDistribution?.map((d) => d.value) || [];
    const statusLabels = targetStatusDistribution?.map((d) => d.label) || [];
    const statusDonutColors = targetStatusDistribution?.map((d) => targetStatusColors[d.label] || '#D0D5DD') || [];
    const filteredEvents = (cumulativeProgress?.events || []).filter((event) => {
        if (event.type === 'Client') return showClientEvents;
        if (event.type === 'Goal' || event.type === 'Target') return showGoalEvents;
        return false;
    });
    const aggregateTargetProgressOption = buildAggregateTargetProgressOption({
        targets: sortedTargetProgress,
        events: (cumulativeProgress?.events || []).filter((event) => {
            if (event.type === 'Client') return showAggregateClientEvents;
            if (event.type === 'Goal') return showAggregateGoalEvents;
            return false;
        }),
    });
    const { option: targetsPassingMasteryOption, totalTargets: totalTargetsWithMastery } = buildTargetsPassingMasteryOption({
        dates: (cumulativeProgress?.data || []).map((p) => p.date),
        targets: sortedTargetProgress,
    });

    return (
        <div className="clinical-insights-drawer clinical-insights-drawer-open goal-progress-tab">
            {/* Filter Bar */}
            <div className="insights-filter-bar insights-filter-bar-spaced insights-filter-bar-sticky">
                <span className="insights-filter-label">Goal Progress</span>

                <div className="insights-time-range-group">
                    {timeRangePresets.map((preset) => (
                        <button
                            key={preset.value}
                            className={`insights-time-range-btn ${activeRangePreset === preset.value ? 'active' : ''}`}
                            onClick={() => handleTimeRangeChange(preset.value)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <div className="insights-date-selector-shell">
                    <DateTypeSelector
                        startName="startDate"
                        endName="endDate"
                        filters={dateSelectorFilters}
                        handleGetDates={handleDateRangeChange}
                        buttonStyles={{ marginTop: 0 }}
                    />
                </div>

                <GraphValueModeToggle dataType={goalDataType} value={valueMode} onChange={setValueMode} />

                <Tooltip
                    title={
                        enableGrouping
                            ? 'Grouping is on. Values are bucketed by day, week, or month based on the selected date range, and each bucket shows the average of the sessions in it.'
                            : 'Grouping is off. Each day is shown as its own point — no averaging is applied.'
                    }
                    arrow
                >
                    <label className="insights-grouping-toggle">
                        <span>Group data</span>
                        <Switch
                            size="small"
                            checked={enableGrouping}
                            onChange={(e) => setEnableGrouping(e.target.checked)}
                        />
                    </label>
                </Tooltip>

                <div className="insights-export-group">
                    <button className="insights-export-btn" onClick={() => handleDownloadImage()}>
                        <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                    </button>
                </div>
            </div>

            {/* Cumulative Goal Progress */}
            <div className="insights-chart-card insights-chart-card-with-overlay">
                <div
                    className={`insights-chart-card-header ${
                        isCollapsed(chartCardKeys.CUMULATIVE) ? 'insights-chart-card-header-collapsed' : ''
                    }`}
                >
                    <div>
                        <div className="insights-chart-title-row">
                            <p className="insights-chart-card-title">Cumulative Goal Progress</p>
                            <ChartInfoPopover
                                title="Goal Progress Guide"
                                items={[
                                    {
                                        label: 'Mastery',
                                        text: 'Shows cumulative mastered target progress over time.',
                                        style: { background: '#0C66E4' },
                                    },
                                    {
                                        label: 'Trend',
                                        text: 'Shows the overall direction of progress when enough data exists.',
                                        style: { background: '#FF6B6B' },
                                    },
                                    {
                                        label: 'Mastery',
                                        text: 'Dashed threshold line showing the mastery benchmark.',
                                        type: 'dash',
                                        style: { borderTopColor: '#00C48C' },
                                    },
                                    {
                                        label: 'Client Events',
                                        text: 'Vertical markers for client-level events within the selected range.',
                                        type: 'vertical',
                                        style: { borderColor: '#0C66E4' },
                                    },
                                    {
                                        label: 'Goal Events',
                                        text: 'Vertical markers for goal-level events within the selected range.',
                                        type: 'vertical',
                                        style: { borderColor: '#2A8E6D' },
                                    },
                                ]}
                            />
                        </div>
                        <p className="insights-chart-card-subtitle insights-chart-card-subtitle-system">
                            {displayMode === displayModeEnums.COUNT
                                ? 'Number of mastered targets over time'
                                : 'Percentage of targets mastered over time'}
                        </p>
                    </div>
                    <div className="insights-chart-card-header-actions">
                        <div className="insights-segmented-control">
                            <button
                                className={`insights-segmented-btn ${displayMode === displayModeEnums.PERCENT ? 'active' : ''}`}
                                onClick={() => setDisplayMode(displayModeEnums.PERCENT)}
                            >
                                %
                            </button>
                            <button
                                className={`insights-segmented-btn ${displayMode === displayModeEnums.COUNT ? 'active' : ''}`}
                                onClick={() => setDisplayMode(displayModeEnums.COUNT)}
                            >
                                Count
                            </button>
                        </div>
                        <ChartCollapseToggle
                            collapsed={isCollapsed(chartCardKeys.CUMULATIVE)}
                            onToggle={() => toggleCard(chartCardKeys.CUMULATIVE)}
                        />
                    </div>
                </div>
                <Collapse in={!isCollapsed(chartCardKeys.CUMULATIVE)}>
                    <CumulativeProgressChart
                        chartRef={cumulativeChartRef}
                        data={cumulativeProgress?.data || []}
                        events={filteredEvents}
                        showMastery={showMastery}
                        showTrendLine={showTrendLine}
                        showClientEvents={showClientEvents}
                        showGoalEvents={showGoalEvents}
                        onToggleMastery={setShowMastery}
                        onToggleTrend={setShowTrendLine}
                        onToggleClientEvents={setShowClientEvents}
                        onToggleGoalEvents={setShowGoalEvents}
                        displayMode={displayMode}
                        goalMastery={goalMastery}
                        totalCount={targetStatusDistribution?.reduce((sum, item) => sum + (item?.value || 0), 0) || 0}
                        onRangeSelect={handleGraphRangeSelect}
                    />
                </Collapse>
                {isSummaryLoading && (
                    <div className="insights-chart-loading-overlay">
                        <CircularProgress size={28} />
                    </div>
                )}
            </div>

            {/* Targets Achieving Mastery by Session — per-bucket count of targets whose
                session value crossed their own mastery threshold that bucket. */}
            {(targetsPassingMasteryOption || isTargetsLoading) && (
                <div className="insights-chart-card insights-chart-card-with-overlay">
                    <div
                        className={`insights-chart-card-header ${
                            isCollapsed(chartCardKeys.TARGETS_PASSING_MASTERY) ? 'insights-chart-card-header-collapsed' : ''
                        }`}
                    >
                        <div>
                            <div className="insights-chart-title-row">
                                <p className="insights-chart-card-title">Targets Achieving Mastery by Session</p>
                                <ChartInfoPopover
                                    title="Session Mastery Guide"
                                    items={[
                                        {
                                            label: 'Targets Achieving Mastery',
                                            text: "Each bar is a session-level snapshot: how many targets reached their own mastery threshold in that specific session. It's not a running total, and it's independent of whether a target's overall status has ever changed to Mastered.",
                                            style: { background: '#12B76A' },
                                        },
                                    ]}
                                />
                            </div>
                            <p className="insights-chart-card-subtitle insights-chart-card-subtitle-system">
                                Targets that reached their mastery threshold in each session
                                {totalTargetsWithMastery ? ` (out of ${totalTargetsWithMastery} tracked)` : ''}
                            </p>
                        </div>
                        <div className="insights-chart-card-header-actions">
                            <button
                                className="insights-export-btn insights-export-btn-inline"
                                onClick={() => handleDownloadImage(targetsPassingMasteryChartRef, 'targets-passing-mastery')}
                            >
                                <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                            </button>
                            <ChartCollapseToggle
                                collapsed={isCollapsed(chartCardKeys.TARGETS_PASSING_MASTERY)}
                                onToggle={() => toggleCard(chartCardKeys.TARGETS_PASSING_MASTERY)}
                            />
                        </div>
                    </div>
                    {targetsPassingMasteryOption && (
                        <Collapse in={!isCollapsed(chartCardKeys.TARGETS_PASSING_MASTERY)}>
                            <ReactECharts
                                ref={targetsPassingMasteryChartRef}
                                option={targetsPassingMasteryOption}
                                notMerge={true}
                                className="insights-chart-medium"
                                opts={{ renderer: 'canvas' }}
                            />
                        </Collapse>
                    )}
                    {isTargetsLoading && (
                        <div className="insights-chart-loading-overlay">
                            <CircularProgress size={28} />
                        </div>
                    )}
                </div>
            )}

            {/* Target Progress */}
            {(targetProgress?.length > 0 || isTargetsLoading) && (
                <div className="insights-chart-card-with-overlay">
                    <p className="insights-section-label insights-section-label-top">Target Progress</p>
                    {isTargetsLoading && targetProgress.length === 0 && (
                        <div className="insights-chart-card insights-chart-card-with-overlay">
                            <div className="insights-chart-loading-overlay insights-chart-loading-overlay-static">
                                <CircularProgress size={28} />
                            </div>
                        </div>
                    )}
                    {aggregateTargetProgressOption && (
                        <div className="insights-chart-card">
                            <div
                                className={`insights-chart-card-header ${
                                    isCollapsed(chartCardKeys.ALL_TARGETS) ? 'insights-chart-card-header-collapsed' : ''
                                }`}
                            >
                                <div>
                                    <div className="insights-chart-title-row">
                                        <p className="insights-chart-card-title">All Targets Progress</p>
                                        <ChartInfoPopover
                                            title="All Targets Chart Guide"
                                            items={[
                                                {
                                                    label: 'Target Lines',
                                                    text: 'Shows session performance for all targets together in one chart.',
                                                    style: { background: '#0C66E4' },
                                                },
                                                {
                                                    label: 'Client Events',
                                                    text: 'Vertical markers for client-level events within the selected range.',
                                                    type: 'vertical',
                                                    style: { borderColor: '#0C66E4' },
                                                },
                                                {
                                                    label: 'Goal Events',
                                                    text: 'Vertical markers for goal-level events within the selected range.',
                                                    type: 'vertical',
                                                    style: { borderColor: '#2A8E6D' },
                                                },
                                            ]}
                                        />
                                    </div>
                                    <p className="insights-chart-card-subtitle">
                                        Combined target performance across the selected date range
                                        {enableGrouping && (
                                            <span className="insights-chart-card-subtitle-note">
                                                {' '}
                                                — values shown are the average of session values within each time bucket.
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div className="insights-chart-card-header-actions">
                                    <button
                                        className="insights-export-btn insights-export-btn-inline"
                                        onClick={() => handleDownloadImage(aggregateTargetChartRef, 'all-targets-progress')}
                                    >
                                        <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                                    </button>
                                    <ChartCollapseToggle
                                        collapsed={isCollapsed(chartCardKeys.ALL_TARGETS)}
                                        onToggle={() => toggleCard(chartCardKeys.ALL_TARGETS)}
                                    />
                                </div>
                            </div>
                            <Collapse in={!isCollapsed(chartCardKeys.ALL_TARGETS)}>
                                <ReactECharts
                                    ref={aggregateTargetChartRef}
                                    option={aggregateTargetProgressOption}
                                    notMerge={true}
                                    className="insights-chart-medium"
                                    opts={{ renderer: 'canvas' }}
                                />
                                <div className="insights-chart-legend insights-chart-legend-small">
                                    <button
                                        className={`insights-chart-legend-item ${showAggregateClientEvents ? 'active' : ''}`}
                                        onClick={() => setShowAggregateClientEvents((prev) => !prev)}
                                    >
                                        <span className="insights-chart-legend-vertical insights-chart-legend-vertical-client" />
                                        <span>Client Events</span>
                                    </button>
                                    <button
                                        className={`insights-chart-legend-item ${showAggregateGoalEvents ? 'active' : ''}`}
                                        onClick={() => setShowAggregateGoalEvents((prev) => !prev)}
                                    >
                                        <span className="insights-chart-legend-vertical insights-chart-legend-vertical-goal" />
                                        <span>Goal Events</span>
                                    </button>
                                </div>
                            </Collapse>
                        </div>
                    )}
                    {sortedTargetProgress.map((target) => {
                        const cardKey = targetCardKey(target.targetId);
                        const fullTarget = canChangeStatus ? loadedGoal?.targets?.find((t) => t?.id === target.targetId) : null;

                        return (
                            <div key={target.targetId} className="target-progress-card">
                                <div
                                    className={`target-progress-card-header ${
                                        isCollapsed(cardKey) ? 'target-progress-card-header-collapsed' : ''
                                    }`}
                                >
                                    <p className="target-progress-card-name">{target.targetName}</p>
                                    <div className="target-progress-card-actions">
                                        {fullTarget ? (
                                            <MasteredTargetTooltip target={fullTarget}>
                                                <GoalTargetStatus
                                                    target={fullTarget}
                                                    goalById={loadedGoal}
                                                    requestParams={requestParams}
                                                    noAction={isTargetStatusLocked(loadedGoal)}
                                                    onSuccess={fetchDrilldown}
                                                />
                                            </MasteredTargetTooltip>
                                        ) : (
                                            <span className={`status-badge ${getStatusClassName(target.status)}`}>
                                                <span className={`status-badge-dot ${getStatusClassName(target.status)}`} />
                                                {target.status}
                                            </span>
                                        )}
                                        <button
                                            className="insights-export-btn insights-export-btn-inline"
                                            onClick={() => handleDownloadTargetImage(target.targetId, target.targetName)}
                                        >
                                            <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                                        </button>
                                        <ChartCollapseToggle collapsed={isCollapsed(cardKey)} onToggle={() => toggleCard(cardKey)} />
                                    </div>
                                </div>
                                <Collapse in={!isCollapsed(cardKey)}>
                                    <TargetProgressChart
                                        chartRef={(node) => {
                                            targetChartRefs.current[target.targetId] = node;
                                        }}
                                        data={target.progressData}
                                        baselinePhase={target.baselinePhase}
                                        dataType={target.dataType}
                                        mastery={target.mastery}
                                        events={target.events || []}
                                    />
                                </Collapse>
                            </div>
                        );
                    })}
                    {isTargetsLoading && targetProgress.length > 0 && (
                        <div className="insights-chart-loading-overlay">
                            <CircularProgress size={28} />
                        </div>
                    )}
                </div>
            )}

            {/* Target Status Distribution */}
            {(statusSeries.length > 0 || isSummaryLoading) && (
                <div className="insights-secondary-grid">
                    <div
                        className={`insights-chart-card insights-chart-card-compact insights-chart-card-with-overlay ${
                            isCollapsed(chartCardKeys.STATUS_DISTRIBUTION) ? 'insights-chart-card-collapsed' : ''
                        }`}
                    >
                        <div
                            className={`insights-chart-card-header insights-chart-card-header-tight ${
                                isCollapsed(chartCardKeys.STATUS_DISTRIBUTION) ? 'insights-chart-card-header-collapsed' : ''
                            }`}
                        >
                            <p className="insights-chart-card-title">Target Status Distribution</p>
                            <div className="insights-chart-card-header-actions">
                                <button
                                    className="insights-export-btn insights-export-btn-inline"
                                    onClick={() => handleDownloadCardImage('target-status-distribution')}
                                >
                                    <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                                </button>
                                <ChartCollapseToggle
                                    collapsed={isCollapsed(chartCardKeys.STATUS_DISTRIBUTION)}
                                    onToggle={() => toggleCard(chartCardKeys.STATUS_DISTRIBUTION)}
                                />
                            </div>
                        </div>
                        <Collapse in={!isCollapsed(chartCardKeys.STATUS_DISTRIBUTION)}>
                            <div className="insights-status-distribution">
                                <div className="insights-status-distribution-chart">
                                    <InsightsDonutChart
                                        chartRef={statusDistributionChartRef}
                                        series={statusSeries}
                                        labels={statusLabels}
                                        colors={statusDonutColors}
                                        height={200}
                                    />
                                </div>
                                <div className="donut-legend insights-status-distribution-legend">
                                    {targetStatusDistribution?.map((item, idx) => (
                                        <div key={idx} className="donut-legend-item">
                                            <div className="donut-legend-left">
                                                <span className={`donut-legend-dot ${getStatusClassName(item.label)}`} />
                                                <span>{item.label}</span>
                                            </div>
                                            <span className="donut-legend-value">{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Collapse>
                        {isSummaryLoading && (
                            <div className="insights-chart-loading-overlay">
                                <CircularProgress size={24} />
                            </div>
                        )}
                    </div>
                    <div
                        className={`insights-chart-card insights-chart-card-compact insights-chart-card-with-overlay ${
                            isCollapsed(chartCardKeys.STAFF_FOCUS) ? 'insights-chart-card-collapsed' : ''
                        }`}
                    >
                        <div
                            className={`insights-chart-card-header insights-chart-card-header-tight ${
                                isCollapsed(chartCardKeys.STAFF_FOCUS) ? 'insights-chart-card-header-collapsed' : ''
                            }`}
                        >
                            <div>
                                <p className="insights-chart-card-title">Staff Focus</p>
                                <p className="insights-chart-card-subtitle">Where staff time is needed right now</p>
                            </div>
                            <div className="insights-chart-card-header-actions">
                                <button
                                    className="insights-export-btn insights-export-btn-inline"
                                    onClick={() => handleDownloadCardImage('staff-focus')}
                                >
                                    <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                                </button>
                                <ChartCollapseToggle
                                    collapsed={isCollapsed(chartCardKeys.STAFF_FOCUS)}
                                    onToggle={() => toggleCard(chartCardKeys.STAFF_FOCUS)}
                                />
                            </div>
                        </div>
                        <Collapse in={!isCollapsed(chartCardKeys.STAFF_FOCUS)}>
                            <StaffFocusSnapshotChart chartRef={staffFocusChartRef} distribution={targetStatusDistribution} />
                        </Collapse>
                        {isSummaryLoading && (
                            <div className="insights-chart-loading-overlay">
                                <CircularProgress size={24} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/** Cumulative Progress — ECharts line chart */
const CumulativeProgressChart = ({
    chartRef,
    data,
    events,
    showMastery,
    showTrendLine,
    showClientEvents,
    showGoalEvents,
    onToggleMastery,
    onToggleTrend,
    onToggleClientEvents,
    onToggleGoalEvents,
    displayMode,
    goalMastery,
    totalCount,
    onRangeSelect,
}) => {
    const categories = useMemo(() => data.map((p) => p.date), [data]);
    const [chartInstance, setChartInstance] = useState(null);
    const [showMasterySeries, setShowMasterySeries] = useState(true);
    const [showTrendSeries, setShowTrendSeries] = useState(true);
    const [showAverageSeries, setShowAverageSeries] = useState(true);
    const hasMasteryLine = Boolean(getGoalMasteryLine(goalMastery, true, totalCount));

    useEffect(() => {
        if (!chartInstance || !categories.length) return;

        const activationTimer = window.setTimeout(() => {
            chartInstance.dispatchAction({
                type: 'takeGlobalCursor',
                key: 'brush',
                brushOption: {
                    brushType: 'lineX',
                    brushMode: 'single',
                },
            });
        }, 0);

        return () => window.clearTimeout(activationTimer);
    }, [categories.length, chartInstance, data, displayMode, events, showMastery, totalCount]);

    const handleBrushEnd = (params) => {
        const area = params?.areas?.[0];
        const range = area?.coordRange;
        if (!Array.isArray(range) || range.length < 2 || !categories.length) return;

        const [rawStart, rawEnd] = range;
        const resolveCategoryIndex = (value) => {
            if (typeof value === 'number') {
                return Math.max(0, Math.min(categories.length - 1, Math.round(value)));
            }

            const categoryIndex = categories.indexOf(value);
            return categoryIndex >= 0 ? categoryIndex : 0;
        };

        const startIndex = Math.min(resolveCategoryIndex(rawStart), resolveCategoryIndex(rawEnd));
        const endIndex = Math.max(resolveCategoryIndex(rawStart), resolveCategoryIndex(rawEnd));
        const startDate = categories[startIndex];
        const endDate = categories[endIndex];

        if (!startDate || !endDate) return;

        const normalizedRange = {
            startDate: moment(startDate, ['YYYY-MM-DD', 'YYYY-MM', 'GGGG-[W]WW'], true)
                .startOf(startDate.includes('-W') ? 'isoWeek' : startDate.length > 7 ? 'day' : 'month')
                .format('YYYY-MM-DD'),
            endDate: moment(endDate, ['YYYY-MM-DD', 'YYYY-MM', 'GGGG-[W]WW'], true)
                .endOf(endDate.includes('-W') ? 'isoWeek' : endDate.length > 7 ? 'day' : 'month')
                .format('YYYY-MM-DD'),
        };

        onRangeSelect?.(normalizedRange);
        chartRef?.current?.getEchartsInstance?.()?.dispatchAction({
            type: 'brush',
            areas: [],
        });
    };

    const option = useMemo(() => {
        if (!data?.length) return null;
        const isPercent = displayMode === displayModeEnums.PERCENT;
        const timeAxisLabel = getTimeAxisLabel(categories);
        const seriesData = data.map((p) => (isPercent ? p.cumulative : p.cumulativeCount));
        const baseYAxisMax = isPercent ? 100 : Math.max(totalCount || 0, ...seriesData, 0);
        const yAxisMax = isPercent ? 100 : Math.max(baseYAxisMax, 1);
        const yAxisDisplayMax = isPercent ? 100 : Math.max(Math.ceil(yAxisMax * 1.12), yAxisMax + 1);
        const eventMarkerY = isPercent ? 96 : Math.max(yAxisMax, yAxisDisplayMax * 0.9);
        const changePointIndexes = getChangePointIndexes(data, displayMode);
        const masteryLine = getGoalMasteryLine(goalMastery, isPercent, data.at(-1)?.totalCount ?? totalCount);
        const eventBuckets = buildEventBuckets(events, categories);
        const eventBucketMap = new Map(eventBuckets.map((bucket) => [bucket.key, bucket]));
        const markLineData = [];
        const trendLine = showTrendLine
            ? buildLinearTrendLine({
                  values: seriesData,
                  maxValue: yAxisMax,
                  decimals: isPercent ? 1 : 2,
              })
            : { seriesData: [] };
        const averageLine = showAverageSeries
            ? buildAverageLine({
                  values: seriesData,
                  decimals: isPercent ? 1 : 2,
              })
            : { value: null };

        if (showAverageSeries && averageLine.value !== null) {
            markLineData.push({
                yAxis: averageLine.value,
                label: {
                    formatter: isPercent ? `Avg (${averageLine.value}%)` : `Avg (${averageLine.value})`,
                    position: 'insideEndBottom',
                    fontSize: 10,
                    color: '#7A5AF8',
                    fontFamily: FONT_FAMILY,
                },
                lineStyle: { color: '#7A5AF8', type: 'dashed', width: 1.5 },
            });
        }

        if (showMastery && masteryLine) {
            markLineData.push({
                yAxis: masteryLine.value,
                label: {
                    formatter: masteryLine.label,
                    position: 'insideEndTop',
                    fontSize: 10,
                    color: '#00C48C',
                    fontFamily: FONT_FAMILY,
                },
                lineStyle: { color: '#00C48C', type: 'dashed', width: 1.5 },
            });
        }

        eventBuckets.forEach((bucket) => {
            markLineData.push({
                xAxis: bucket.key,
                lineStyle: {
                    color: bucket.color,
                    type: 'dashed',
                    width: 1.5,
                },
                label: { show: false },
            });
        });

        const eventMarkerSeries = eventBuckets.some((bucket) => bucket.count > 1)
            ? [
                  {
                      name: 'Event Counts',
                      type: 'scatter',
                      silent: true,
                      data: eventBuckets
                          .filter((bucket) => bucket.count > 1)
                          .map((bucket) => ({
                              value: [bucket.key, eventMarkerY],
                              count: bucket.count,
                              itemStyle: { color: bucket.color },
                              label: {
                                  show: true,
                                  formatter: `${bucket.count}`,
                                  color: '#FFFFFF',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  fontFamily: FONT_FAMILY,
                              },
                          })),
                      symbol: 'circle',
                      symbolSize: 18,
                      z: 6,
                  },
              ]
            : [];
        const eventHoverSeries =
            eventBuckets.length > 0
                ? [
                      {
                          name: 'Event Hover',
                          type: 'bar',
                          data: categories.map((category) => (eventBucketMap.has(category) ? yAxisDisplayMax : 0)),
                          barWidth: 12,
                          barGap: '-100%',
                          itemStyle: {
                              color: 'rgba(0, 0, 0, 0)',
                          },
                          emphasis: {
                              itemStyle: {
                                  color: 'rgba(0, 0, 0, 0)',
                              },
                          },
                          tooltip: {
                              show: false,
                          },
                          z: 1,
                      },
                  ]
                : [];

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' },
                extraCssText: `box-shadow:none;background:transparent;border:none;padding:0;font-family:${EVENT_TOOLTIP_FONT_FAMILY};`,
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
                formatter: (params) => {
                    if (!params?.length) return '';
                    const axisValue = params[0]?.axisValue;
                    const linePoint = params.find((param) => param.seriesType === 'line');
                    const value = linePoint?.value;
                    const label = formatInsightDate(axisValue, 'MMM YYYY', 'DD MMM YYYY');
                    const eventTooltip = buildBucketTooltipHtml(eventBucketMap.get(axisValue));
                    const summaryCard = `
                        <div style="padding:12px 8px; background:white; border-radius:8px; box-shadow:0 0 6px 0 rgba(138, 138, 138, 0.24); margin-bottom:${eventTooltip ? '8px' : '0'}; font-family:${EVENT_TOOLTIP_FONT_FAMILY};">
                            <p style="color:#0C66E4; font-size:12px; font-weight:600; line-height:18px; margin:0 0 4px;">${label}</p>
                            <div style="display:flex; align-items:center; gap:6px;">
                                <p style="color:#0C66E4; margin:0;">●</p>
                                <p style="color:#344054; font-size:12px; font-weight:400; line-height:18px; margin:0;">
                                    Mastery:
                                    <span style="color:#344054; font-size:12px; font-weight:600; line-height:18px;"> ${value}${isPercent ? '%' : ''}</span>
                                </p>
                            </div>
                        </div>
                    `;

                    return `${summaryCard}${eventTooltip}`;
                },
            },
            toolbox: {
                show: false,
                feature: {},
            },
            grid: { left: '7%', right: '4%', top: '5%', bottom: '17%', containLabel: true },
            brush: {
                toolbox: false,
                xAxisIndex: 'all',
                brushType: 'lineX',
                brushMode: 'single',
                transformable: false,
                throttleType: 'debounce',
                throttleDelay: 150,
                brushStyle: {
                    borderWidth: 1,
                    color: 'rgba(12, 102, 228, 0.12)',
                    borderColor: '#0C66E4',
                },
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: categories,
                name: timeAxisLabel,
                nameLocation: 'middle',
                nameGap: 34,
                nameTextStyle: {
                    color: '#667085',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                },
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisTick: { show: false },
                axisLabel: {
                    color: '#98A2B3',
                    fontSize: 11,
                    fontFamily: FONT_FAMILY,
                    formatter: (v) => formatInsightDate(v),
                },
                splitNumber: data.length > 14 ? 7 : undefined,
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: yAxisDisplayMax,
                minInterval: isPercent ? undefined : 1,
                name: isPercent ? 'Mastery Rate (%)' : 'Mastered Targets',
                nameLocation: 'middle',
                nameGap: 54,
                nameRotate: 90,
                nameTextStyle: {
                    color: '#667085',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                },
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#98A2B3',
                    fontSize: 11,
                    fontFamily: FONT_FAMILY,
                    formatter: (value) => (isPercent ? `${value}%` : `${Math.round(value)}`),
                },
                splitLine: { lineStyle: { color: '#F2F4F7' } },
            },
            series: [
                ...(showMasterySeries
                    ? [
                          {
                              name: 'Mastery',
                              type: 'line',
                              data: seriesData,
                              smooth: false,
                              symbol: 'circle',
                              showSymbol: true,
                              symbolSize: (value, params) => (changePointIndexes.has(params?.dataIndex) ? 6 : 0),
                              lineStyle: { color: '#0C66E4', width: 2.5 },
                              itemStyle: {
                                  color: '#0C66E4',
                                  borderColor: '#fff',
                                  borderWidth: 2,
                              },
                              areaStyle: {
                                  color: {
                                      type: 'linear',
                                      x: 0,
                                      y: 0,
                                      x2: 0,
                                      y2: 1,
                                      colorStops: [
                                          { offset: 0, color: 'rgba(12,102,228,0.10)' },
                                          { offset: 1, color: 'rgba(12,102,228,0.01)' },
                                      ],
                                  },
                              },
                          },
                      ]
                    : []),
                ...(markLineData.length > 0
                    ? [
                          {
                              name: 'Annotations',
                              type: 'line',
                              data: seriesData,
                              silent: true,
                              symbol: 'none',
                              lineStyle: {
                                  opacity: 0,
                              },
                              itemStyle: {
                                  opacity: 0,
                              },
                              emphasis: {
                                  disabled: true,
                              },
                              tooltip: {
                                  show: false,
                              },
                              markLine: {
                                  symbol: 'none',
                                  data: markLineData,
                              },
                              z: 0,
                          },
                      ]
                    : []),
                ...(showTrendSeries && trendLine.seriesData.length > 0
                    ? [
                          {
                              name: 'Trend',
                              type: 'line',
                              data: trendLine.seriesData,
                              smooth: true,
                              symbol: 'none',
                              silent: true,
                              lineStyle: {
                                  color: '#FF6B6B',
                                  width: 2,
                                  opacity: 0.95,
                              },
                              z: 3,
                          },
                      ]
                    : []),
                ...eventHoverSeries,
                ...eventMarkerSeries,
            ],
        };
    }, [categories, data, displayMode, events, goalMastery, showAverageSeries, showMastery, showMasterySeries, showTrendLine, showTrendSeries, totalCount]);

    if (!option)
        return (
            <div className="insights-empty">
                <p>No progress data</p>
            </div>
        );

    return (
        <>
            <ReactECharts
                ref={chartRef}
                option={option}
                notMerge={true}
                style={{ height: 280, width: '100%' }}
                opts={{ renderer: 'canvas' }}
                onChartReady={(instance) => setChartInstance(instance)}
                onEvents={{ brushEnd: handleBrushEnd }}
            />
            <div className="insights-chart-legend">
                <button
                    className={`insights-chart-legend-item ${showMasterySeries ? 'active' : ''}`}
                    onClick={() => setShowMasterySeries((prev) => !prev)}
                >
                    <span className="insights-chart-legend-line" style={{ background: '#0C66E4' }} />
                    <span>Mastery</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showTrendLine && showTrendSeries ? 'active' : ''}`}
                    onClick={() => {
                        const nextValue = !(showTrendLine && showTrendSeries);
                        setShowTrendSeries(nextValue);
                        onToggleTrend?.(nextValue);
                    }}
                >
                    <span className="insights-chart-legend-line" style={{ background: '#FF6B6B' }} />
                    <span>Trend</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showAverageSeries ? 'active' : ''}`}
                    onClick={() => setShowAverageSeries((prev) => !prev)}
                >
                    <span className="insights-chart-legend-dash insights-chart-legend-dash-average" />
                    <span>Average</span>
                </button>
                {hasMasteryLine && showMastery && (
                    <button className="insights-chart-legend-item active" onClick={() => onToggleMastery?.(false)}>
                        <span className="insights-chart-legend-dash" />
                        <span>Goal mastery</span>
                    </button>
                )}
                {hasMasteryLine && !showMastery && (
                    <button className="insights-chart-legend-item" onClick={() => onToggleMastery?.(true)}>
                        <span className="insights-chart-legend-dash" />
                        <span>Goal mastery</span>
                    </button>
                )}
                {showClientEvents && (
                    <button className="insights-chart-legend-item active" onClick={() => onToggleClientEvents?.(false)}>
                        <span className="insights-chart-legend-vertical" style={{ borderColor: '#0C66E4' }} />
                        <span>Client Events</span>
                    </button>
                )}
                {!showClientEvents && (
                    <button className="insights-chart-legend-item" onClick={() => onToggleClientEvents?.(true)}>
                        <span className="insights-chart-legend-vertical" style={{ borderColor: '#0C66E4' }} />
                        <span>Client Events</span>
                    </button>
                )}
                {showGoalEvents && (
                    <button className="insights-chart-legend-item active" onClick={() => onToggleGoalEvents?.(false)}>
                        <span className="insights-chart-legend-vertical" style={{ borderColor: '#2A8E6D' }} />
                        <span>Goal Events</span>
                    </button>
                )}
                {!showGoalEvents && (
                    <button className="insights-chart-legend-item" onClick={() => onToggleGoalEvents?.(true)}>
                        <span className="insights-chart-legend-vertical" style={{ borderColor: '#2A8E6D' }} />
                        <span>Goal Events</span>
                    </button>
                )}
            </div>
        </>
    );
};

const StaffFocusSnapshotChart = ({ distribution = [], chartRef }) => {
    const chartConfig = useMemo(() => {
        const counts = distribution.reduce((acc, item) => {
            acc[item.label] = item.value || 0;
            return acc;
        }, {});

        const data = [
            {
                label: 'Needs Attention',
                value: (counts.PENDING || 0) + (counts.BASELINE || 0) + (counts.HOLD || 0) + (counts.DISCONTINUED || 0),
                color: '#F79009',
                description: 'Pending, baseline, hold, discontinued',
            },
            {
                label: 'In Progress',
                value: counts.TEACHING || 0,
                color: '#0C66E4',
                description: 'Targets currently being worked on',
            },
            {
                label: 'Achieved',
                value: counts.MASTERED || 0,
                color: '#12B76A',
                description: 'Mastered targets, maintenance included',
            },
        ];

        const total = data.reduce((sum, item) => sum + item.value, 0);

        return {
            total,
            data: data.map((item) => ({
                ...item,
                percent: total ? Math.round((item.value / total) * 100) : 0,
            })),
        };
    }, [distribution]);

    if (!chartConfig.total) return null;

    const option = {
        grid: { left: 4, right: 56, top: 8, bottom: 0, containLabel: true },
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                const item = chartConfig.data[params.dataIndex];
                return `
                    <div style="padding:8px 10px; font-family:${FONT_FAMILY};">
                        <div style="font-size:12px; font-weight:600; color:#1D2939; margin-bottom:4px;">${item.label}</div>
                        <div style="font-size:12px; color:#475467; margin-bottom:2px;">Targets: <b>${item.value}</b> (${item.percent}%)</div>
                        <div style="font-size:12px; color:#667085;">${item.description}</div>
                    </div>
                `;
            },
            extraCssText: 'padding:0; border:none; box-shadow:0 0 6px 0 rgba(138, 138, 138, 0.24); border-radius:8px;',
        },
        xAxis: {
            type: 'value',
            max: chartConfig.total,
            splitLine: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
        },
        yAxis: {
            type: 'category',
            data: chartConfig.data.map((item) => item.label),
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: '#344054',
                fontSize: 12,
                fontFamily: FONT_FAMILY,
                fontWeight: 600,
            },
        },
        series: [
            {
                type: 'bar',
                barWidth: 16,
                showBackground: true,
                backgroundStyle: {
                    color: '#F2F4F7',
                    borderRadius: [999, 999, 999, 999],
                },
                data: chartConfig.data.map((item) => ({
                    value: item.value,
                    itemStyle: {
                        color: item.color,
                        borderRadius: [999, 999, 999, 999],
                    },
                    label: {
                        show: true,
                        position: 'right',
                        formatter: `${item.value}  ${item.percent}%`,
                        color: '#1D2939',
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: FONT_FAMILY,
                    },
                })),
            },
        ],
    };

    return (
        <ReactECharts ref={chartRef} option={option} notMerge={true} style={{ height: 220, width: '100%' }} opts={{ renderer: 'canvas' }} />
    );
};
